import { createContext, useContext, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ConsultationContext = createContext();

export function useConsultation() {
  return useContext(ConsultationContext);
}

export function ConsultationProvider({ children }) {
  const [open, setOpen] = useState(false);

  return (
    <ConsultationContext.Provider value={{ open, setOpen }}>
      {children}
      <ConsultationDialog open={open} onOpenChange={setOpen} />
    </ConsultationContext.Provider>
  );
}

function ConsultationDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '', message: '', inquiry_type: 'consultation'
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success('Thank you! We will be in touch shortly.');
      onOpenChange(false);
      setForm({ name: '', email: '', phone: '', company: '', message: '', inquiry_type: 'consultation' });
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] border-wc-border p-0 rounded-none" data-testid="consultation-dialog">
        <div className="p-8">
          <DialogHeader>
            <div className="w-12 h-px bg-wc-gold mb-4" />
            <DialogTitle className="font-heading text-2xl font-normal text-wc-black">
              Schedule a Consultation
            </DialogTitle>
            <p className="font-body text-sm text-wc-muted mt-2">
              Tell us about your business needs and we'll reach out promptly.
            </p>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="consultation-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-body text-xs uppercase tracking-[0.1em] text-wc-muted">Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                  placeholder="Your full name"
                  className="rounded-none border-wc-border font-body text-sm focus-visible:ring-wc-gold"
                  data-testid="consultation-name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-xs uppercase tracking-[0.1em] text-wc-muted">Email *</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  placeholder="your@email.com"
                  className="rounded-none border-wc-border font-body text-sm focus-visible:ring-wc-gold"
                  data-testid="consultation-email"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-body text-xs uppercase tracking-[0.1em] text-wc-muted">Phone</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  placeholder="(555) 000-0000"
                  className="rounded-none border-wc-border font-body text-sm focus-visible:ring-wc-gold"
                  data-testid="consultation-phone"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-xs uppercase tracking-[0.1em] text-wc-muted">Company</Label>
                <Input
                  value={form.company}
                  onChange={(e) => update('company', e.target.value)}
                  placeholder="Your business name"
                  className="rounded-none border-wc-border font-body text-sm focus-visible:ring-wc-gold"
                  data-testid="consultation-company"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-body text-xs uppercase tracking-[0.1em] text-wc-muted">Inquiry Type</Label>
              <Select value={form.inquiry_type} onValueChange={(v) => update('inquiry_type', v)}>
                <SelectTrigger className="rounded-none border-wc-border font-body text-sm" data-testid="consultation-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="general">General Inquiry</SelectItem>
                  <SelectItem value="consultation">Free 15-Minute Consultation</SelectItem>
                  <SelectItem value="cleanup">$200 Book Cleanup</SelectItem>
                  <SelectItem value="bookkeeping">Bookkeeping</SelectItem>
                  <SelectItem value="wine">Vintage Wine & Champagne</SelectItem>
                  <SelectItem value="webdesign">Web Design</SelectItem>
                  <SelectItem value="events">Events & Vendor Connections</SelectItem>
                  <SelectItem value="it">IT & Programming</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-body text-xs uppercase tracking-[0.1em] text-wc-muted">Message *</Label>
              <Textarea
                value={form.message}
                onChange={(e) => update('message', e.target.value)}
                placeholder="Tell us about your business and how we can help..."
                rows={4}
                className="rounded-none border-wc-border font-body text-sm focus-visible:ring-wc-gold resize-none"
                data-testid="consultation-message"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-wc-black text-white py-3.5 font-body text-xs uppercase tracking-[0.15em] hover:bg-wc-charcoal transition-colors duration-300 disabled:opacity-50 mt-2"
              data-testid="consultation-submit"
            >
              {loading ? 'Submitting...' : 'Submit Inquiry'}
            </button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
